// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import AtomLineChart from '@/components/atoms/LineChart';
import { getYearSelected } from '@/store/studies/selectors';
import {
  selectCompanyHistory,
  selectStudyYearCategoryCompaniesLoadingHistory,
} from '@/store/study-year-category-companies/selectors';
import { studyYearCategoryCompanies } from '@/store/study-year-category-companies/thunks';

interface HistoricalDataDialogContentProps {
  selectedRow: any;
  selectedSegment: string;
  studyYearCategoryCompanyId?: string; // Add this prop to identify which company's history to fetch
}

interface ChartDataPoint {
  x: Date;
  y: number;
}

const HistoricalDataDialogContent: React.FC<HistoricalDataDialogContentProps> = ({
  selectedRow,
  selectedSegment,
  studyYearCategoryCompanyId,
}) => {
  const dispatch = useDispatch();

  // Updated selectors to use history endpoint
  const companyHistory = useSelector(selectCompanyHistory);
  const companyHistoryLoading = useSelector(selectStudyYearCategoryCompaniesLoadingHistory);
  const useYearSelected = useSelector(getYearSelected);

  // Local state
  const [historicalChartData, setHistoricalChartData] = useState([]);

  const transformCompanyData = (historyData: any, valueKey: string): ChartDataPoint[] => {
    if (!historyData || !historyData.children || !Array.isArray(historyData.children)) {
      return [];
    }

    // Transform history data - if you have actual historical years, use them
    // Otherwise, use children data points with month indexing
    return historyData.children.map((item, index) => ({
      x: new Date(useYearSelected?.year || new Date().getFullYear(), index, 1), // Use month as index for multiple data points
      y: item[valueKey] || 0,
    }));
  };

  // Fetch company history when component mounts or when studyYearCategoryCompanyId changes
  useEffect(() => {
    if (studyYearCategoryCompanyId) {
      dispatch(
        studyYearCategoryCompanies.getStudyYearCategoryCompanyHistory({
          studyYearCategoryCompanyId,
        })
      );
    }
  }, [dispatch, studyYearCategoryCompanyId]);

  useEffect(() => {
    if (companyHistory && companyHistory.children && Array.isArray(companyHistory.children)) {
      // Transform the company history data for the chart
      const cData = [
        {
          id: 'revenue',
          label: 'Company Revenue',
          color: '#2196f3',
          data: transformCompanyData(companyHistory, 'revenue'),
        },
        {
          id: 'market_share',
          label: 'Market Share',
          color: '#4caf50',
          data: transformCompanyData(companyHistory, 'market_share'),
        },
      ];
      setHistoricalChartData(cData);
    } else {
      // Set empty data structure if no history data
      setHistoricalChartData([
        {
          id: 'revenue',
          label: 'Company Revenue',
          color: '#2196f3',
          data: [],
        },
        {
          id: 'market_share',
          label: 'Market Share',
          color: '#4caf50',
          data: [],
        },
      ]);
    }
  }, [companyHistory, useYearSelected]);

  return (
    <div>
      <h3>Historical data</h3>
      <div style={{ height: '50vh', width: '100%' }}>
        {companyHistoryLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <p>Loading company history...</p>
          </div>
        ) : historicalChartData &&
          historicalChartData.length > 0 &&
          historicalChartData.some(series => series.data.length > 0) ? (
          <AtomLineChart
            series={historicalChartData}
            width={800}
            height={400}
            showGrid={true}
            showTooltip={true}
            showLegend={true}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <p>No company history available</p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              {selectedSegment} segment history for {useYearSelected?.year}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricalDataDialogContent;
